import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { skipWhile, switchMap, of, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PortalService } from '../services/portal.service';
import { environment } from '../../environments/environment';

/**
 * Guard to restrict access to purchasing page.
 * Blocks access if:
 * - User is not an admin, OR
 * - Environment is VX/Eagle production (adminvx.careerscope.com), OR
 * - Portal has hidePurchasing flag set to true
 * 
 * Note: Only blocks in production VX/Eagle, not in local emulators or staging
 */
export const purchasingAccessGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const portalService = inject(PortalService);

  // Block access only in VX/Eagle PRODUCTION environment (adminvx.careerscope.com)
  // Allow in local emulators, staging, and preview staging
  if (environment.vx && environment.production) {
    // Redirect to dashboard
    return auth.user$.pipe(
      skipWhile(user => user === null),
      switchMap(user => {
        if (user?.lastPortalId) {
          router.navigateByUrl(user.lastPortalId + '/dash');
        } else {
          router.navigateByUrl('/');
        }
        return of(false);
      })
    );
  }

  // Get portalId from route params
  const portalId = route.params['portalId'];

  // Check admin access and portal hidePurchasing flag
  return auth.user$.pipe(
    skipWhile(user => user === null),
    switchMap(user => {
      if (!user?.admin) {
        // Redirect to dashboard if user is not an admin
        if (user?.lastPortalId) {
          router.navigateByUrl(user.lastPortalId + '/dash');
        } else {
          router.navigateByUrl('/');
        }
        return of(false);
      }

      // Check if portal has hidePurchasing flag
      if (portalId) {
        // Get portal from service - it should already be loaded for the current portal
        return portalService.portal.pipe(
          take(1),
          switchMap(portal => {
            // If portal is not loaded yet or doesn't match, allow access (will be checked on next navigation)
            // If portal has hidePurchasing set to true, block access
            if (portal && portal.id === portalId && portal.hidePurchasing === true) {
              if (user?.lastPortalId) {
                router.navigateByUrl(user.lastPortalId + '/dash');
              } else {
                router.navigateByUrl('/');
              }
              return of(false);
            }
            // Allow access if portal doesn't have hidePurchasing or it's false
            return of(true);
          })
        );
      }

      return of(true);
    })
  );
};
